import React, { Component } from 'react';
import axios from 'axios';
import { Typography, withStyles, CircularProgress } from '@material-ui/core';
import { red } from '@material-ui/core/colors';
import { Auth } from 'aws-amplify';
import moment from 'moment';
import ConfirmPurchase from '../components/ConfirmPurchase';
import ConfirmBankPurchase from '../components/ConfirmBankPurchase';
import { getYearGroups, hasValidZonePricing } from '../helpers/ticket';
import { contactOperatorString } from '../helpers/contact';
import BuyAgainForm from '../components/BuyAgainForm';
import { getAcademicYear } from '../helpers/date';

const styles = theme => ({
    root: {
        border: '0',
        maxWidth: '800px',
        margin: '40px auto',
        position: 'relative',
        [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            margin: '20px 15px'
        }
    },
    error: {
        margin: '20px 0 0 5px',
        color: red[500],
        minHeight: '20px',
        textAlign: 'center'
    },
    spinner: {
        marginLeft: 'calc(50% - 22px)',
        marginTop: '60px'
    },
    availabilityWarning: {
        background: 'red',
        textAlign: 'center',
        marginBottom: 20,
        padding: 5
    },
    availabilityWarningHeading: {
        color: 'white',
        fontWeight: 'bold'
    },
    availabilityWarningText: {
        color: 'white'
    }
});

class TryAgain extends Component {
    constructor(props) {
        super(props);

        this.state = {
            error: '',
            passengerData: {
                firstName: '',
                lastName: '',
                id: '',
                stop: '',
                dest: '',
                photo: null,
                startDate: null,
                startTime: '',
                yr: '',
                zone: '',
                zoneStop: ''
            },
            passengerSubmitted: false,
            ticket: null,
            client: null,
            service: null,
            serviceAvailability: null,
            isLoading: true
        };
    }

    async componentDidMount() {
        const {
            match: {
                params: { encryptedTicketId }
            }
        } = this.props;

        // validate encrypted ticket id param
        if (encryptedTicketId.split(':').length !== 3) {
            this.setState({
                isLoading: false,
                error: 'Invalid request'
            });
            return;
        }

        let client;

        try {
            const oldTicket = await this.getOldTicketData(encryptedTicketId);
            client = await this.getClientData(oldTicket.client_id);

            if (
                process.env.REACT_APP_DISABLE_EXPIRED_TICKETS === '1' &&
                getAcademicYear() > oldTicket.ticket_year
            ) {
                throw new Error('Expired ticket year');
            }

            const service = this.extractService(client, oldTicket.service_id);
            const ticket = await this.getTicketData(oldTicket, service);
            let serviceAvailability = null;

            if (
                ticket.type === 'single' ||
                ticket.type === 'return' ||
                ticket.type === 'week' ||
                ticket.type === '4_week'
            ) {
                serviceAvailability = await this.getServiceAvailability(
                    client.slug,
                    oldTicket.service_id,
                    oldTicket.ticket_year
                );
            }

            this.setState({
                client,
                ticket,
                service,
                serviceAvailability,
                passengerData: {
                    firstName: oldTicket.passenger.first_name,
                    lastName: oldTicket.passenger.last_name,
                    stop: oldTicket.stop,
                    photo: oldTicket.passenger.photo || null,
                    id: oldTicket.ext_id || null,
                    startDate: null,
                    startTime: '',
                    yr: oldTicket.yr,
                    dest: oldTicket.dest,
                    zone: oldTicket.zone,
                    zoneStop: oldTicket.zone_stop
                },
                isLoading: false,
                error: ''
            });
        } catch (e) {
            console.error(e);
            let errorMessage =
                'There was a problem retrieving the ticket. Please try again later or contact our support team.';

            if (e.message === 'Ticket unavailable') {
                errorMessage = `This ticket type is now sold out. Please contact ${contactOperatorString(
                    client
                )} for more information regarding ticket availability.`;
            }

            if (e.message === 'Ticket disabled') {
                errorMessage = `Ticket disabled by your transport provider. Please contact ${contactOperatorString(
                    client
                )} for more information.`;
            }

            if (e.message === 'Ticket expired') {
                errorMessage = `This ticket type has a fixed expiry date which has now expired. Please contact ${contactOperatorString(
                    client
                )} for an alternative ticket type to purchase.`;
            }

            if (e.message === 'Zonal pricing misconfiguration') {
                errorMessage = `Zonal pricing on this ticket has not been configured. Please contact ${contactOperatorString(
                    client
                )} to request they update their pricing.`;
            }

            if (e.message === 'Expired ticket year') {
                errorMessage = `This ticket type was valid for a previous year and has now expired. Please contact ${contactOperatorString(
                    client
                )} for an alternative ticket type to purchase.`;
            }

            if (e.message === 'No service') {
                errorMessage = `This service has either been discontinued or does not exist. Please contact ${contactOperatorString(
                    client
                )} for an alternative ticket type to purchase.`;
            }

            this.setState({
                isLoading: false,
                error: errorMessage
            });
        }
    }

    getOldTicketData = async encryptedTicketId => {
        const currentSession = await Auth.currentSession();

        const headers = {
            Authorization: currentSession.getIdToken().getJwtToken()
        };

        const response = await axios.get(
            [
                process.env.REACT_APP_PAYMENT_API_ENDPOINT,
                'customer-ticket',
                'encrypted',
                encryptedTicketId
            ].join('/'),
            {
                headers
            }
        );

        const ticketData = response.data;

        ['client_id', 'service_id', 'name_key', 'user_id'].forEach(prop => {
            if (ticketData[prop].startsWith('HIDDEN_')) {
                ticketData[prop] = ticketData[prop].replace('HIDDEN_', '');
            }
        });

        return ticketData;
    };

    getClientData = async clientSlug => {
        const response = await axios.get(
            `${process.env.REACT_APP_API_ENDPOINT}/client/${clientSlug}`
        );

        return response.data;
    };

    getTicketData = async ({ ticket_id: ticketId }, service) => {
        const currentSession = await Auth.currentSession();
        const headers = {
            Authorization: currentSession.getIdToken().getJwtToken()
        };

        const response = await axios.get(
            `${process.env.REACT_APP_TICKET_API_ENDPOINT}/ticket/${ticketId}/detailed`,
            { headers }
        );

        if (!response.data) {
            throw new Error();
        }

        const ticket = response.data;

        if (!ticket) {
            throw new Error();
        }

        if (ticket.is_enabled === false) {
            throw new Error('Ticket disabled');
        }

        if (ticket.available === false) {
            throw new Error('Ticket unavailable');
        }

        if (ticket.pricing_model === 'Zonal') {
            if (!hasValidZonePricing(ticket, service)) {
                throw new Error('Zonal pricing misconfiguration');
            }
        }

        if (
            ticket.expiry &&
            process.env.REACT_APP_DISABLE_EXPIRED_TICKETS === '1'
        ) {
            const now = moment();
            const expiry = moment(ticket.expiry);

            if (now.toISOString() > expiry.toISOString()) {
                throw new Error('Ticket expired');
            }
        }

        return ticket;
    };

    getServiceAvailability = async (clientSlug, serviceId, year) => {
        const currentSession = await Auth.currentSession();

        const headers = {
            Authorization: currentSession.getIdToken().getJwtToken()
        };

        const response = await axios.get(
            `${process.env.REACT_APP_PAYMENT_API_ENDPOINT}/service-availability/${clientSlug}/${serviceId}/${year}`,
            {
                headers
            }
        );

        return response.data;
    };

    handleChange = event => {
        const { passengerData } = this.state;
        this.setState({
            passengerData: {
                ...passengerData,
                [event.target.name]: event.target.value
            },
            error: ''
        });
    };

    handleDateChange = date => {
        const { passengerData } = this.state;
        this.setState({
            passengerData: {
                ...passengerData,
                startDate: date
            },
            error: ''
        });
    };

    handleSubmitPassenger = event => {
        event.preventDefault();
        this.setState({
            passengerSubmitted: true
        });
        // @NOTE: Hack to jump user to top of page on submit, as the route doesn't change
        // resulting in unexpected landing half way down subsequent view.
        window.scrollTo(0, 0);
    };

    handleEditPassenger = () => {
        this.setState({
            passengerSubmitted: false
        });
    };

    passengerValid = () => {
        const { passengerData, service, client } = this.state;

        let stopSelectionValid = true;
        let yearGroupValid = true;
        let destValid = true;
        let zoneSelectionValid = true;

        const useZones = service.zones && passengerData.zone;

        if (service && 'stops' in service && !useZones) {
            stopSelectionValid = service.stops.includes(passengerData.stop);
        }

        if (useZones) {
            const zone =
                passengerData.zone &&
                service.zones.find(
                    serviceZone => serviceZone.name === passengerData.zone
                );

            if (zone && (!zone.stops || !zone.stops.length)) {
                zoneSelectionValid = true;
            } else if (
                zone &&
                zone.stops &&
                zone.stops.includes(passengerData.zoneStop)
            ) {
                zoneSelectionValid = true;
            } else {
                zoneSelectionValid = false;
            }
        }

        if (service && getYearGroups(service.yr_groups).length) {
            if (!passengerData.yr) {
                yearGroupValid = false;
            }
        }

        if (
            service &&
            'destinations' in service &&
            service.destinations.length
        ) {
            destValid = service.destinations.includes(passengerData.dest);
        }

        const requireStudentId = client.features.require_id === 'student';
        const isIdValid =
            !requireStudentId || (requireStudentId && passengerData.id);

        return (
            stopSelectionValid &&
            passengerData.firstName &&
            passengerData.lastName &&
            yearGroupValid &&
            destValid &&
            zoneSelectionValid &&
            isIdValid
        );
    };

    showConfirmPurchase = () => {
        const { ticket, passengerSubmitted, service } = this.state;

        return this.passengerValid() && ticket && service && passengerSubmitted;
    };

    formatRemainingTickets = ticket => {
        const ticketWord = ticket.remaining > 1 ? 'tickets' : 'ticket';
        return `Only ${ticket.remaining} ${ticketWord} left, hurry!`;
    };

    extractService = (client, serviceId) => {
        const { services = [] } = client;

        if (!services.length) {
            throw new Error();
        }

        const service = services.find(item => item.id === serviceId);

        if (!service) {
            throw new Error('No service');
        }

        return service;
    };

    render() {
        const {
            passengerData,
            ticket,
            client,
            service,
            serviceAvailability,
            error,
            isLoading
        } = this.state;

        const { classes } = this.props;

        if (isLoading) {
            return (
                <div className={classes.root}>
                    <CircularProgress
                        id="loading-spinner"
                        className={classes.spinner}
                    />
                </div>
            );
        }

        if (error) {
            return (
                <div className={classes.root}>
                    <Typography className={classes.error} variant="body2">
                        {error}
                    </Typography>
                </div>
            );
        }

        const disablePhoto =
            client && client.features && client.features.disable_photo === true;

        const requireStudentId =
            client &&
            client.features &&
            client.features.require_id === 'student';

        const showAvailabilityWarning =
            'remaining' in ticket &&
            ticket.remaining > 0 &&
            ticket.remaining <= 10;

        let confirmPurchaseComponent;

        if (ticket && ticket.payment_type === 'bank') {
            confirmPurchaseComponent = (
                <ConfirmBankPurchase
                    ticket={ticket}
                    passenger={passengerData}
                    client={client}
                    service={service}
                    handleEdit={this.handleEditPassenger}
                    buyAgain
                    disablePhoto={disablePhoto}
                    requireStudentId={requireStudentId}
                />
            );
        } else {
            confirmPurchaseComponent = (
                <ConfirmPurchase
                    ticket={ticket}
                    passenger={passengerData}
                    client={client}
                    service={service}
                    handleEdit={this.handleEditPassenger}
                    buyAgain
                    disablePhoto={disablePhoto}
                    requireStudentId={requireStudentId}
                />
            );
        }

        return (
            <div className={classes.root}>
                {showAvailabilityWarning && (
                    <div className={classes.availabilityWarning}>
                        <Typography
                            className={classes.availabilityWarningHeading}
                            variant="body2"
                        >
                            {this.formatRemainingTickets(ticket)}
                        </Typography>
                        <Typography
                            className={classes.availabilityWarningText}
                            variant="body1"
                        >
                            If you need more tickets than available please do
                            not purchase this ticket.
                        </Typography>
                    </div>
                )}
                {this.showConfirmPurchase() ? (
                    <div>{confirmPurchaseComponent}</div>
                ) : (
                    <BuyAgainForm
                        ticket={ticket}
                        passenger={passengerData}
                        serviceAvailability={serviceAvailability}
                        service={service}
                        handleChange={this.handleChange}
                        handleDateChange={this.handleDateChange}
                        handleSubmit={this.handleSubmitPassenger}
                        requireStudentId={requireStudentId}
                    />
                )}
            </div>
        );
    }
}

export default withStyles(styles)(TryAgain);
